<template>
  <div class="chart-container">
    <Line :data="chartData" :options="chartOptions" />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Line } from 'vue-chartjs';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const { locale, t } = useI18n();

const props = defineProps({
  data: {
    type: Array,
    required: true
  },
  title: {
    type: String,
    default: 'Timeline'
  },
  range: {
    type: String,
    default: '1d'
  }
});

const chartData = computed(() => {
  // Determine if we should show hourly format
  // Check if range is '1d' OR if data points are hourly (for custom single-day ranges)
  let showHourlyFormat = props.range === '1d';

  // If not '1d', check if data suggests hourly granularity
  if (!showHourlyFormat && props.data.length >= 2) {
    const first = new Date(props.data[0]._id);
    const second = new Date(props.data[1]._id);
    const diffMs = Math.abs(second - first);
    const oneHourMs = 60 * 60 * 1000;

    // If data points are approximately 1 hour apart (within 10 minutes tolerance)
    // then it's hourly granularity
    if (Math.abs(diffMs - oneHourMs) < 10 * 60 * 1000) {
      showHourlyFormat = true;
    }
  }
  
  return {
    labels: props.data.map(d => {
      // d._id è già una stringa ISO date dal backend
      const date = new Date(d._id);
      if (isNaN(date.getTime())) {
        return d._id; // fallback se la data non è valida
      }
      const currentLocale = locale.value === 'it' ? 'it-IT' : 'en-US';
      
      // Se il range è giornaliero (1d), mostra ore:minuti
      if (showHourlyFormat) {
        return date.toLocaleTimeString(currentLocale, { hour: '2-digit', minute: '2-digit' });
      }
      
      // Altrimenti mostra giorno e mese
      return date.toLocaleDateString(currentLocale, { month: 'short', day: 'numeric' });
    }),
    datasets: [
      {
        label: t('common.conversations'),
        data: props.data.map(d => d.conversations || 0),
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y'
      },
      {
        label: t('common.messages'),
        data: props.data.map(d => (d.messagesReceived || 0) + (d.messagesSent || 0)),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y1'
      }
    ]
  };
});

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: {
    mode: 'index',
    intersect: false
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      labels: {
        color: '#9ca3af',
        padding: 10,
        font: {
          size: 11
        },
        boxWidth: 12,
        usePointStyle: true
      }
    },
    tooltip: {
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      padding: 10,
      titleColor: '#fff',
      bodyColor: '#fff'
    }
  },
  scales: {
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      beginAtZero: true,
      title: {
        display: true,
        text: t('common.conversations'),
        color: '#f59e0b'
      },
      ticks: {
        color: '#9ca3af',
        precision: 0
      },
      grid: {
        color: 'rgba(156, 163, 175, 0.1)'
      }
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      beginAtZero: true,
      title: {
        display: true,
        text: t('common.messages'),
        color: '#3b82f6'
      },
      ticks: {
        color: '#9ca3af',
        precision: 0
      },
      grid: {
        drawOnChartArea: false
      }
    },
    x: {
      ticks: {
        color: '#9ca3af'
      },
      grid: {
        display: false
      }
    }
  }
};
</script>

<style scoped>
.chart-container {
  height: 200px;
  width: 100%;
}
</style>
